import { useState } from 'react'
import { Card, Chip, EmptyState, LoadingRows, relativeTime, secondaryBtn } from '../family-finance/shared'
import type { FantasyProvider } from '../../types/fantasy-football'
import { connectProvider, disconnectProvider, syncConnection } from '../../lib/fantasy-api'
import type { FantasyState } from './useFantasy'

const PROVIDER_META: Record<FantasyProvider, { label: string; icon: string; blurb: string }> = {
  yahoo: {
    label: 'Yahoo Fantasy',
    icon: '🟣',
    blurb: 'OAuth 2.0 — read-only league, roster, and player access. You approve on Yahoo; no password is shared with this app.',
  },
  espn: {
    label: 'ESPN Fantasy',
    icon: '🔴',
    blurb: 'Linked via ESPN account authorization — league, roster, and free-agent pools sync on demand.',
  },
}

export default function LeaguesTab({ state }: { state: FantasyState }) {
  const { loading, connections, refresh } = state
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleConnect(provider: FantasyProvider) {
    setBusy(`connect-${provider}`)
    setError(null)
    try {
      await connectProvider(provider)
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Connection failed')
    } finally {
      setBusy(null)
    }
  }

  async function handleSync(id: string) {
    setBusy(`sync-${id}`)
    setError(null)
    try {
      await syncConnection(id)
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed')
    } finally {
      setBusy(null)
    }
  }

  async function handleDisconnect(id: string) {
    setBusy(`disconnect-${id}`)
    setError(null)
    try {
      await disconnectProvider(id)
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Disconnect failed')
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <LoadingRows rows={4} />

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      <Card
        title="Connect a fantasy platform"
        subtitle="Registered API access — the same model FantasyPros uses: you authorize on the provider's site, this app receives a scoped read token"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(Object.keys(PROVIDER_META) as FantasyProvider[]).map((provider) => {
            const meta = PROVIDER_META[provider]
            return (
              <div key={provider} className="bg-slate-100/60 dark:bg-slate-900/60 rounded-lg p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{meta.icon}</span>
                  <span className="text-slate-900 dark:text-white font-medium">{meta.label}</span>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs flex-1">{meta.blurb}</p>
                <button
                  onClick={() => handleConnect(provider)}
                  disabled={busy !== null}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {busy === `connect-${provider}` ? 'Connecting…' : `Connect ${meta.label}`}
                </button>
              </div>
            )
          })}
        </div>
      </Card>

      <Card title="Connected leagues" subtitle="Rosters and available players refresh from these on sync">
        {connections.length === 0 ? (
          <EmptyState icon="🔗" message="No leagues connected yet." />
        ) : (
          <div className="space-y-3">
            {connections.map((conn) => (
              <div
                key={conn.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-100/60 dark:bg-slate-900/60 rounded-lg px-4 py-3"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl">{PROVIDER_META[conn.provider].icon}</span>
                  <div className="min-w-0">
                    <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{conn.leagueName}</p>
                    <p className="text-slate-500 text-xs">
                      {PROVIDER_META[conn.provider].label} · {conn.teamCount} teams · {conn.scoring} ·{' '}
                      {conn.teamName}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Chip tone={conn.status === 'connected' ? 'green' : conn.status === 'expired' ? 'amber' : 'red'}>
                    {conn.status}
                  </Chip>
                  <span className="text-slate-500 text-xs">synced {relativeTime(conn.lastSync)}</span>
                  <button
                    onClick={() => handleSync(conn.id)}
                    disabled={busy !== null}
                    className={secondaryBtn}
                  >
                    {busy === `sync-${conn.id}` ? 'Syncing…' : 'Sync'}
                  </button>
                  <button
                    onClick={() => handleDisconnect(conn.id)}
                    disabled={busy !== null}
                    className="px-3 py-1.5 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 text-sm rounded-lg transition-colors"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="How linking works">
        <ul className="text-slate-500 dark:text-slate-400 text-sm space-y-2 list-disc list-inside">
          <li>
            <span className="text-slate-800 dark:text-slate-200">Yahoo</span> uses OAuth 2.0: you sign in on Yahoo's own page and
            grant read-only fantasy scope. The backend stores the refresh token; this app never sees your password.
          </li>
          <li>
            <span className="text-slate-800 dark:text-slate-200">ESPN</span> links through ESPN account authorization for private
            leagues; public leagues need only the league ID.
          </li>
          <li>
            Syncs pull rosters, free agents, and matchup schedules so rankings reflect who is actually
            available <span className="text-slate-800 dark:text-slate-200">in your league</span>, not a generic pool.
          </li>
        </ul>
      </Card>
    </div>
  )
}
